import React, { useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';
import PropTypes from 'prop-types';
import { DataTable, Icon } from '@openedx/paragon';
import { Link } from 'react-router-dom';
import { Download } from '@openedx/paragon/icons';
import { analyticsDataTableKeys, COURSE_TYPES, ALL_COURSES } from '../data/constants';

import { useEnterpriseAnalyticsData, usePaginatedData } from '../data/hooks';
import EnterpriseDataApiService from '../../../data/services/EnterpriseDataApiService';

const AnalyticsTable = ({
  name,
  tableColumns,
  tableTitle,
  tableSubtitle,
  entityId,
  startDate,
  endDate,
  enterpriseId,
  groupUUID,
  budgetUUID,
  courseType,
  course,
  trackCsvDownloadClick,
}) => {
  const intl = useIntl();
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  const {
    isFetching, data,
  } = useEnterpriseAnalyticsData({
    enterpriseCustomerUUID: enterpriseId,
    key: analyticsDataTableKeys[name],
    startDate,
    endDate,
    groupUUID,
    budgetUUID,
    // pages index from 1 in backend, frontend components index from 0
    currentPage: currentPage + 1,
    pageSize,
    courseType,
    course,
  });

  const csvDownloadOptions = {
    start_date: startDate,
    end_date: endDate,
  };

  if (courseType && courseType !== COURSE_TYPES.ALL_COURSE_TYPES) {
    csvDownloadOptions.course_type = courseType;
  }

  if (course?.value && course?.value !== ALL_COURSES.value) {
    csvDownloadOptions.course_key = course.value;
  }

  if (budgetUUID) {
    csvDownloadOptions.budget_uuid = budgetUUID;
  }

  if (groupUUID) {
    csvDownloadOptions.group_uuid = groupUUID;
  }

  const CSVDownloadURL = EnterpriseDataApiService.getAnalyticsCSVDownloadURL(
    analyticsDataTableKeys[name],
    enterpriseId,
    csvDownloadOptions,
  );

  const fetchData = useCallback(
    (args) => {
      if (args.pageIndex !== currentPage) {
        setCurrentPage(args.pageIndex);
      }
    },
    [currentPage],
  );

  const paginatedData = usePaginatedData(data);

  return (
    <div className="analytics-data-table mt-4">
      <div className="analytics-datatable-container">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h2 className="analytics-header-title mb-0">{tableTitle}</h2>
          <Link
            to={CSVDownloadURL}
            target="_blank"
            className={`btn btn-sm btn-primary rounded-0  ${!data?.results?.length ? 'disabled' : ''}`}
            onClick={() => trackCsvDownloadClick(entityId)}
          >
            <Icon src={Download} className="me-2" />
            <FormattedMessage
              id="adminPortal.analytics.downloadCSV.button"
              defaultMessage="Download {respectiveTableName} CSV"
              description="Button to download CSV for respective table"
              values={{ respectiveTableName: name.charAt(0).toUpperCase() + name.slice(1) }}
            />
          </Link>
        </div>

        {tableSubtitle && (
        <p className="analytics-header-subtitle mb-3">{tableSubtitle}</p>
        )}

        <DataTable
          isLoading={isFetching}
          isPaginated
          manualPagination
          initialState={{
            pageSize,
            pageIndex: 0,
          }}
          itemCount={paginatedData.itemCount}
          pageCount={paginatedData.pageCount}
          fetchData={fetchData}
          data={paginatedData.data}
          columns={tableColumns}
        >
          <DataTable.TableControlBar />
          <DataTable.Table />
          <DataTable.EmptyTable
            content={intl.formatMessage({
              id: 'advance.analytics.table.empty.label',
              defaultMessage: 'No results found.',
              description: 'Message displayed when the table has no data.',
            })}
          />
          <DataTable.TableFooter />
        </DataTable>
      </div>
    </div>
  );
};

AnalyticsTable.defaultProps = {
  groupUUID: undefined,
  courseType: undefined,
  course: undefined,
};

AnalyticsTable.propTypes = {
  name: PropTypes.string.isRequired,
  tableColumns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tableTitle: PropTypes.string.isRequired,
  tableSubtitle: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  enterpriseId: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  groupUUID: PropTypes.string,
  budgetUUID: PropTypes.string,
  courseType: PropTypes.string,
  course: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  trackCsvDownloadClick: PropTypes.func,
};

export default AnalyticsTable;
